import { useCallback, useState } from 'react'
import { useAppSelector } from 'reduxTyped'

import {
  createGoalFromFormValues,
  mapGoalToFormValues,
  mergeExitActionWithGoal,
  mergeFormValuesWithGoal,
} from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/lib/mappers'
import { ConfigFormValues } from 'apps/aiAutomation/components/EngagementGoalEditor/components/LeadQualEditor/lib/types'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  LeadQualEditorStep,
  StepState,
  SubmitResult,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import { AiAutomationEngagementGoalSelectors } from 'apps/aiAutomation/store/engagementGoal'
import {
  GoalType,
  LeadQualExitAction,
  LeadQualGoal,
} from 'shared/api/requests/aiEngagementGoal/schemas'

export const useLeadQualEditor = ({ onCreate }: { onCreate: () => void }) => {
  const { goalId, goal, save } = useGoalEditorContext(GoalType.LEAD_QUAL)

  const isLoading = useAppSelector((state) =>
    goal
      ? AiAutomationEngagementGoalSelectors.getIsItemProcessingAction(state, goal.id)
      : AiAutomationEngagementGoalSelectors.getIsNewItemCreating(state),
  )

  const [configData, setConfigData] = useState<ConfigFormValues | null>(
    goal ? mapGoalToFormValues(goal) : null,
  )

  const [exitActionData, setExitActionData] = useState<LeadQualExitAction | null>(
    goal ? goal.data.settings.exit_action : null,
  )

  const [stepsState, setStepsState] = useState<StepState<LeadQualEditorStep>>({
    current: goal ? LeadQualEditorStep.PUBLISH : LeadQualEditorStep.CONFIG,
    completed: {
      [LeadQualEditorStep.CONFIG]: Boolean(goal),
      [LeadQualEditorStep.EXIT_ACTION]: Boolean(goal),
      [LeadQualEditorStep.PUBLISH]: false,
    },
  })

  const completeStep = useCallback((step: LeadQualEditorStep) => {
    let nextStep: LeadQualEditorStep

    switch (step) {
      case LeadQualEditorStep.CONFIG: {
        nextStep = LeadQualEditorStep.EXIT_ACTION
        break
      }
      case LeadQualEditorStep.EXIT_ACTION: {
        nextStep = LeadQualEditorStep.PUBLISH
        break
      }
      case LeadQualEditorStep.PUBLISH: {
        nextStep = LeadQualEditorStep.PUBLISH
        break
      }
    }

    setStepsState((prev) => ({
      ...prev,
      completed: {
        ...prev.completed,
        [step]: true,
      },
      current: nextStep,
    }))
  }, [])

  const onConfigFinish = useCallback(
    (config: ConfigFormValues) => {
      setConfigData(config)

      completeStep(LeadQualEditorStep.CONFIG)
    },
    [completeStep],
  )

  const onExitActionFinish = useCallback(
    async (action: LeadQualExitAction | null) => {
      if (!configData) {
        return
      }

      setExitActionData(action)

      if (goal) {
        await save(mergeExitActionWithGoal(mergeFormValuesWithGoal(goal, configData), action))
      } else {
        await save(
          mergeExitActionWithGoal(createGoalFromFormValues({ id: goalId, ...configData }), action),
        )

        onCreate()
      }

      completeStep(LeadQualEditorStep.EXIT_ACTION)
    },
    [completeStep, configData, goal, goalId, onCreate, save],
  )

  const combineAllData: (
    config: SubmitResult<ConfigFormValues>,
    action: SubmitResult<LeadQualExitAction>,
  ) => SubmitResult<LeadQualGoal> = useCallback(
    (config, action) => {
      if (!goal || !config.isValid || !action.isValid) {
        return { isValid: false }
      }

      return {
        isValid: true,
        data: mergeExitActionWithGoal(mergeFormValuesWithGoal(goal, config.data), action.data),
      }
    },
    [goal],
  )

  return {
    stepsState,
    onConfigFinish,
    isLoading,
    configData,
    exitActionData,
    onExitActionFinish,
    combineAllData,
  }
}
